import { useEcomTracking } from '~/composables/gtm/useEcomTracking'
import { useGtmDataLayer } from '~/composables/gtm/useGtmDataLayer'
import { useTrackUser } from '~/composables/gtm/useTrackUser'
import { getOgTitle } from '~/composables/seo/opengraph'

function datalayerContainsEvent(dataLayer, event) {
  if (!dataLayer || !Array.isArray(dataLayer)) {
    return false
  }

  return dataLayer.some(item => item.event === event)
}

/**
 * GTM page view tracking. Single composable returning trackPageView and trackErrorPageView.
 */
export function useTrackPageView() {
  const { dataLayer } = useGtmDataLayer()
  const { trackUser, trackLogin } = useTrackUser()
  const { trackItemView } = useEcomTracking()

  function addUserToDatalayer(userId) {
    trackUser(userId)

    if (typeof window !== 'undefined' && window.location.hash.includes('#state=')) {
      trackLogin(userId)
    }
  }

  function trackPageView(pageGroup, page, userId, price = null, product = null) {
    if (import.meta.server) {
      return
    }

    let category = ''

    if (page?.breadcrumbs?.length > 2) {
      category = page.breadcrumbs[page.breadcrumbs.length - 2].text
    }
    else {
      try {
        category = page?.breadcrumbs?.[page.breadcrumbs.length - 1]?.text ?? page?.title ?? ''
      }
      catch {
        category = page?.title ?? ''
      }
    }

    if (category === undefined) {
      category = ''
    }

    const pageView = {
      event: 'virtual_pageview',
      responseStatus: 200,
      content_group: pageGroup,
      content_category: category,
      page_title: getOgTitle(page),
      page_location: document.location.href,
    }

    const pushPageView = () => {
      addUserToDatalayer(userId)
      dataLayer?.push(pageView)

      if (price && product) {
        trackItemView(product, price, 1, page)
      }
    }

    if (!datalayerContainsEvent(dataLayer, 'cf_after_consent_update')) {
      window.addEventListener('cf_consent_loaded', (event) => {
        event.target.removeEventListener('cf_consent_loaded', event, false)
        pushPageView()
      })
    }
    else {
      pushPageView()
    }
  }

  function trackErrorPageView(statusCode, pageTitle = null, pageLocation = null) {
    if (import.meta.server) {
      return
    }

    const payload = {
      event: 'virtual_pageview',
      responseStatus: statusCode,
      content_group: 'Error',
      content_category: String(statusCode),
      page_title: pageTitle ?? `${statusCode} - Page not found`,
      page_location: pageLocation ?? (typeof document !== 'undefined' ? document.location.href : ''),
    }

    const push = () => dataLayer?.push(payload)

    if (!datalayerContainsEvent(dataLayer, 'cf_after_consent_update')) {
      window.addEventListener('cf_consent_loaded', (event) => {
        event.target.removeEventListener('cf_consent_loaded', event, false)
        push()
      })
    }
    else {
      push()
    }
  }

  return {
    trackPageView,
    trackErrorPageView,
  }
}
